import { isFeedReader } from '../../pages/feed/feedDetailModel';
import { isNoticeReader } from '../../features/articles/readerRoutes';
import { isSponsorshipPath } from '../../features/sponsorship/model';
import { isLegalDocumentPath } from '../../features/legal/routes';
import {
  isPublicProfileSummaryPath,
  isPublicProfileRecordsPath,
  isPublicProfileTastePath,
  isPublicProfileBadgesPath,
} from '../../features/profile/routes';
import { isMemberReviewSurface } from '../../utils/reviewRoutePaths';
import { pageFrameStyle } from './PageFrame';
import { isAlbumDetailRoute } from '../../pages/review/components/album/albumHeaderModel';
import { browseChrome } from '../../features/browse/chrome';
import HomeHeader from './HomeHeader';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { REFERRER_TYPE, PAGE_TYPE } from '../../constants/gaEnums';
import { trackMenuClick } from '../../lib/gaEvents';
import { getPageTypeFromPath } from '../../lib/gaParams';
import { MdManageAccounts } from 'react-icons/md';
import { Search } from '@mui/icons-material';
import { Box, useMediaQuery } from '@mui/material';
import logo from '../../assets/logo_black.png';
import logoWhite from '../../assets/logo_white.png';
import { useUserStore } from '../../stores/userStore';
import HeaderMobile from './HeaderMobile';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import HeaderSearchBar from '../common/HeaderSearchBar';
import TabletSearchBar from '../common/TabletSearchBar';
import HeaderProfileButton from '../common/HeaderProfileButton';
import ColorSchemeButton from '../common/ColorSchemeButton';
import theme from '../../theme';
import { useColorScheme } from '../../contexts/ColorSchemeContext';

const Header = () => {
  const { t } = useWebLocale();
  const isAdmin = useUserStore((state) => state.isAdmin);
  const user = useUserStore((state) => state.user);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { resolvedMode } = useColorScheme();
  const [showSearch, setShowSearch] = useState(false);
  const location = useLocation();
  const currentPageType = getPageTypeFromPath(location.pathname);

  if (
    /^\/(?:login(?:\/(?:success|error|apple\/return))?|signup(?:\/detail)?)\/?$/.test(
      location.pathname,
    ) ||
    /^\/password-(change|forgot|reset)\/?$/.test(location.pathname) ||
    isNoticeReader(location.pathname) ||
    /^\/explore\/timeline\/?$/.test(location.pathname) ||
    location.pathname === '/feed' ||
    location.pathname === '/feed/' ||
    isFeedReader(location.pathname) ||
    isSponsorshipPath(location.pathname) ||
    (!(isAdmin && new URLSearchParams(location.search).get('manage') === '1') &&
      isLegalDocumentPath(location.pathname)) ||
    (!(isAdmin && new URLSearchParams(location.search).get('manage') === '1') &&
      /^\/contact\/?$/.test(location.pathname)) ||
    /^\/notifications\/?$/.test(location.pathname) ||
    /^\/mypage(?:\/[^/]+)?\/?$/.test(location.pathname) ||
    isPublicProfileSummaryPath(location.pathname) ||
    isPublicProfileRecordsPath(location.pathname) ||
    isPublicProfileTastePath(location.pathname) ||
    isPublicProfileBadgesPath(location.pathname)
  )
    return (
      <div className="pp-my-global-header">
        <HomeHeader />
      </div>
    );

  if (isMemberReviewSurface(location.pathname))
    return (
      <div className="pp-member-global-header">
        <HomeHeader detail="album" />
      </div>
    );

  if (isAlbumDetailRoute(location.pathname))
    return (
      <div className="pp-album-global-header">
        <HomeHeader detail="album" />
      </div>
    );

  if (location.pathname === '/' || location.pathname === '/explore')
    return <HomeHeader explore={location.pathname === '/explore'} />;

  const chrome = browseChrome(location.pathname);
  if (['artist', 'playlist'].includes(chrome?.detail))
    return (
      <div className={`pp-${chrome.detail}-global-header`}>
        <HomeHeader {...chrome} />
      </div>
    );
  if (chrome) return <HomeHeader {...chrome} />;

  if (isMobile) {
    return <HeaderMobile />;
  }

  const navLinkStyle = {
    color: theme.palette.black[6],
    textDecoration: 'none',
    fontWeight: 400,
    fontSize: 16,
    whiteSpace: 'nowrap',
    '&:hover': {
      color: theme.palette.black[4],
    },
  };

  const iconBtnStyle = {
    color: theme.palette.black[3],
    background: 'none',
    border: 'none',
    padding: { md: '4px', lg: '10px' },
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: 24,
  };

  const handleNavClick = () => {
    if (showSearch) {
      setShowSearch(false);
    }
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        width: '100%',
        backgroundColor: theme.palette.surface.main,
        zIndex: 1201,
        borderBottom: `1px solid ${theme.palette.grey.line}`,
        transition: 'background-color 160ms ease, border-color 160ms ease',
      }}
    >
      <Box
        className="pp-site-header-inner pp-page-frame"
        style={pageFrameStyle}
        sx={{
          maxWidth: 1180,
          height: 60,
          display: { md: 'grid', lg: 'flex' },
          gridTemplateColumns: { md: '1fr auto 1fr', lg: 'none' },
          alignItems: 'center',
          justifyContent: { lg: 'flex-start' },
          gap: { lg: 3 },
          mx: 'auto',
          px: { md: 2.5, lg: 0 },
        }}
      >
        {/* 로고 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 60,
            flexShrink: 0,
            justifySelf: { md: 'start', lg: 'auto' },
            pl: { md: 0, lg: '6px' },
          }}
        >
          <Link
            to="/"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              window.scrollTo({ top: 0, behavior: 'auto' });
            }}
          >
            <Box
              component="img"
              src={resolvedMode === 'dark' ? logoWhite : logo}
              alt="logo"
              sx={{ width: { xs: 91, md: 104, lg: 91 }, height: { xs: 24, md: 27, lg: 24 } }}
            />
          </Link>
        </Box>

        {/* 메뉴 */}
        <Box
          component="nav"
          sx={{
            display: 'flex',
            gap: { md: 1.5, lg: 2.5 },
            alignItems: 'center',
            flexWrap: 'nowrap',
            flexShrink: 0,
            height: 60,
            justifySelf: { md: 'center', lg: 'auto' },
          }}
        >
          <Box component={Link} to="/explore" onClick={handleNavClick} sx={navLinkStyle}>
            {t('explore')}
          </Box>
          <Box
            component={Link}
            to="/albums"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              trackMenuClick(
                { targetPageType: PAGE_TYPE.REVIEWS_MAIN, pageType: currentPageType },
                user,
              );
            }}
            sx={navLinkStyle}
          >
            앨범
          </Box>
          <Box
            component={Link}
            to="/reviews"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              trackMenuClick(
                { targetPageType: PAGE_TYPE.REVIEWS_MAIN, pageType: currentPageType },
                user,
              );
            }}
            sx={navLinkStyle}
          >
            리뷰
          </Box>
          <Box
            component={Link}
            to="/lists"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              trackMenuClick(
                { targetPageType: PAGE_TYPE.LISTS_MAIN, pageType: currentPageType },
                user,
              );
            }}
            sx={navLinkStyle}
          >
            플리
          </Box>
          <Box
            component={Link}
            to="/feed"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={handleNavClick}
            sx={navLinkStyle}
          >
            피드
          </Box>

          <Box
            component={Link}
            to="/notice"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              trackMenuClick(
                { targetPageType: PAGE_TYPE.NOTICE_MAIN, pageType: currentPageType },
                user,
              );
            }}
            sx={navLinkStyle}
          >
            공지
          </Box>
          <Box
            component={Link}
            to="/contact"
            state={{ referrerType: REFERRER_TYPE.MENU }}
            onClick={() => {
              handleNavClick();
              trackMenuClick({ targetPageType: 'contact', pageType: currentPageType }, user);
            }}
            sx={navLinkStyle}
          >
            문의
          </Box>
        </Box>

        {/* 검색창 (lg) */}
        {!isTablet && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <HeaderSearchBar pageType={currentPageType} />
          </Box>
        )}

        {/* 아이콘 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            gap: { md: 1, lg: 0 },
            justifySelf: { md: 'end', lg: 'auto' },
          }}
        >
          {/* 검색 아이콘 (md) */}
          {isTablet && (
            <Box
              component="button"
              onClick={() => setShowSearch(!showSearch)}
              sx={iconBtnStyle}
              title="검색"
            >
              <Search sx={{ fontSize: 24 }} />
            </Box>
          )}
          <ColorSchemeButton
            className="header-color-scheme-toggle"
            iconSize={24}
            sx={iconBtnStyle}
          />
          {isAdmin && (
            <Box
              component={Link}
              to="/admin"
              onClick={handleNavClick}
              sx={iconBtnStyle}
              title="관리자 페이지"
            >
              <MdManageAccounts size={24} />
            </Box>
          )}
          <HeaderProfileButton variant={isTablet ? 'tablet' : 'desktop'} onClick={handleNavClick} />
        </Box>
      </Box>

      {/* 검색창 드롭다운 (md) */}
      {isTablet && (
        <TabletSearchBar
          isOpen={showSearch}
          onClose={() => setShowSearch(false)}
          pageType={currentPageType}
        />
      )}
    </Box>
  );
};

export default Header;
